import { Auth } from "aws-amplify";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import OTPInput from "react-otp-input";
import { useLocation, useNavigate } from "react-router-dom";
import { getUserAuthenticationHookDetails } from "../../services/AuthService";
import { deleteUnConfirmedUser } from "../../services/UserService";
import LoadingSpinner from "../../utils/components/common/loadingSpinner/LoadingSpinner";
import { useEffectOnce } from "../../utils/utils";
import "./Auth.scss";
import LeftPanel from "./LeftPanel";

const EmailOTP = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation("auth");
  const [otp, setOTP] = useState("");
  const [otpError, setOTPError] = useState(false);
  const [terms, setTerms] = useState(false);
  const [termsError, setTermsError] = useState(false);
  const [otpVerifyError, setOTPVerifyError] = useState(false);
  const [otpVerifyErrorMessage, setOTPVerifyErrorMessage] = useState("");
  const [resendOTP, setResendOTP] = useState(false);
  const [signupDisable, setSignupDisable] = useState(true);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLaoding, setIsLoading] = useState(false);

  const confirmEmail = async () => {
    if (otp.length < 6) {
      setOTPError(true);
      return false;
    }
    if (!terms) {
      setTermsError(true);
      return false;
    }
    setIsSubmitted(true);
    setIsLoading(true);
    setOTPVerifyError(false);

    const queryParam = {
      accesstoken: location?.state?.jwtToken,
      attribute: "email",
      action: "VerifyOTP",
      code: otp,
    };
    getUserAuthenticationHookDetails(queryParam)
      .then(function (response) {
        setIsSubmitted(false);
        setIsLoading(false);
        if (response.status === 200) {
          localStorage.setItem("keepmelogin", "false");
          sessionStorage.setItem("keepmelogin", "signin");
          localStorage.removeItem("unconfirmed_email");
          navigate("/");
        }
      })
      .catch(function (error) {
        // console.log(error);
        setIsSubmitted(false);
        setIsLoading(false);
        switch (error.response.data.code) {
          case "CodeMismatchException": {
            setOTPVerifyErrorMessage("Incorrect Email Security Code!");
            setOTPVerifyError(true);
            break;
          }
          case "LimitExceededException": {
            setOTPVerifyErrorMessage(
              "Attempt limit exceeded, please try after some time."
            );
            setOTPVerifyError(true);
            break;
          }
          case "ExpiredCodeException": {
            setOTPVerifyErrorMessage(
              "Security code expired, please click resend"
            );
            setOTPVerifyError(true);
            break;
          }
          default:
            setOTPVerifyErrorMessage("");
        }
      });
  };
  const resendConfirmationCode = async () => {
    setIsLoading(true);
    setOTPVerifyError(false);
    const queryParam = {
      accesstoken: location?.state?.jwtToken,
      attribute: "email",
      action: "SendOTP",
    };
    getUserAuthenticationHookDetails(queryParam)
      .then(function (response) {
        setIsLoading(false);
        setResendOTP(true);
        setTimeout(() => {
          setResendOTP(false);
        }, 5000);
      })
      .catch(function (error) {
        setIsLoading(false);
        setResendOTP(false);
        switch (error.response.data.code) {
          case "LimitExceededException": {
            setOTPVerifyErrorMessage(
              "Attempt limit exceeded, please try after some time."
            );
            setOTPVerifyError(true);
            break;
          }
          default:
            setOTPVerifyErrorMessage("");
        }
      });
  };

  const handleKeypress = (e) => {
    if (e.key === "Enter") {
      if (!signupDisable) confirmEmail();
    }
  };

  const handleBackToSignUp = async () => {
    setIsLoading(true);
    await Auth.signOut();
    const param = {
      attribute: "email",
      attribute_value: location?.state?.email,
    };
    deleteUnConfirmedUser(param)
      .then((response) => {
        setIsLoading(false);
        if (response.status === 200) {
          localStorage.removeItem("unconfirmed_email");
          if (location?.state?.inviteid) {
            navigate("/signup/" + location?.state?.inviteid, {
              state: location?.state,
            });
          } else {
            navigate("/signup", { state: location?.state });
          }
        }
      })
      .catch((err) => {
        setIsLoading(false);
        // console.log(err);
      });
  };

  useEffect(() => {
    if (location.state === null) {
      setTimeout(() => {
        navigate("/signup");
      }, 0);
    }
  }, [location.state, navigate]);

  useEffectOnce(() => {
    return async () => {
      const unconfirmed_email = localStorage.getItem("unconfirmed_email");
      if (unconfirmed_email) {
        await Auth.signOut();

        if (location?.state?.inviteid) {
          navigate("/signup/" + location?.state?.inviteid);
        } else {
          navigate("/signup");
        }
      }
    };
  });

  return (
    <>
      <section className="authContainer">
        <LeftPanel />

        <div className="rightPanel">
          <img
            id="logo"
            src={require("../../assets/images/chorus-rgb.png")}
            className="img-fluid logo mb-4"
            alt="chorus"
          />
          <div
            className="container-fluid pt-5 mt-5 flex-fill"
            onKeyDown={handleKeypress}
          >
            <div className="fieldset d-flex flex-column text-center justify-content-center mb-4 align-items-center">
              <p
                className="fs-6 mb-0 text-dark main-text"
                id="email-enter-code-text"
              >
                {t("securityCodeOnEmail")}
              </p>
              <p className="fs-7" id="code-sent-text">
                A security code is sent to{" "}
                {location?.state?.email || t("yourEmail")}
              </p>
            </div>
            <div className="fieldset d-flex flex-column justify-content-center mb-4 otpInputWrapper">
              <OTPInput
                value={otp}
                className="otpInput"
                focusStyle={{
                  border: "1px solid #CFD3DB",
                  outline: "none",
                }}
                onChange={(val) => {
                  setOTP(val);
                  setOTPError(false);
                  setOTPVerifyError(false);
                  if (val.length < 6 || !terms) {
                    setSignupDisable(true);
                  } else {
                    setSignupDisable(false);
                  }
                }}
                separator={<span style={{ width: "8px" }}> </span>}
                shouldAutoFocus={true}
                numInputs={6}
                isInputNum={true}
                isDisabled={false}
                isInputSecure={true}
              />
              {otpError && (
                <span className="validationError">
                  {t("enterSecurityCode")}
                </span>
              )}
            </div>
            <div className="fieldset d-flex flex-row justify-content-between align-items-center mb-4">
              <button
                onClick={handleBackToSignUp}
                id="wrongemail"
                className="linkBtn"
              >
                {t("enteredWrongEmailId")}
              </button>
              <button
                onClick={resendConfirmationCode}
                id="resendcode"
                name="resendcode"
                className="linkBtn"
              >
                {t("resendCode")}
              </button>
            </div>
            <div className="fieldset d-flex flex-column justify-content-between align-items-start mb-4">
              <div className="form-check">
                <input
                  className="form-check-input"
                  onChange={(e) => {
                    setTerms(e.target.checked);
                    setTermsError(false);
                    if (e.target.checked && otp.length === 6) {
                      setSignupDisable(false);
                    } else {
                      setSignupDisable(true);
                    }
                  }}
                  type="checkbox"
                  value=""
                  id="termsAndCond"
                />
                <label id="termsAndCond_text" className="fs-8 withCheckbox">
                  I agree to the<a className="linkRef">terms of service</a> and
                  <a className="linkRef">privacy policy</a>
                </label>
              </div>
              {termsError && (
                <span className="validationError">{t("acceptTerms")}</span>
              )}
            </div>
            {resendOTP && (
              <div className="fieldset mb-4">
                <p
                  className="fs-7 mb-0 successMsg text-center"
                  id="code-resent-text"
                >
                  {"codeSentTo"} {location.state?.email || t("yourEmail")}
                </p>
              </div>
            )}
            {otpVerifyError && (
              <div className="fieldset mb-4">
                <p
                  id="code_error"
                  className="fs-7 mb-0 validationError text-center"
                >
                  {otpVerifyErrorMessage}
                </p>
              </div>
            )}
            <div className="fieldset mb-4">
              <button
                type="submit"
                id="signup"
                disabled={signupDisable || isSubmitted}
                className={
                  signupDisable || isSubmitted ? "submitBtn dark" : "submitBtn"
                }
                onClick={confirmEmail}
              >
                {isSubmitted ? (
                  <span>
                    {t("loading")}
                    <div
                      className="spinner-border spinner-border-sm"
                      role="status"
                    ></div>
                  </span>
                ) : (
                  <span>{t("signUp")}</span>
                )}
              </button>
            </div>
          </div>
          <div className="fieldset text-center" id="copyright-text">
            {t("rightReservedText")}
          </div>
        </div>
        {isLaoding && <LoadingSpinner />}
      </section>
    </>
  );
};

export default EmailOTP;
